import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Typography, Box, Tooltip, IconButton, Button } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import PlumbingIcon from '@mui/icons-material/Plumbing';
import BaseCard from './BaseCard';
import { statBoxStyle, statValueStyle, statLabelStyle } from './cardStyles';
import { ZIMA_BLUE } from '../../../dashboardColors';
import AppModal from '../../../../Modals/AppModal';

const PipeCoverageCard = ({ user, projectData, data, t, units = 'm', unitFactor, updateProjectPipeCoverage }) => {
  const { covered: coveredPipes, total: totalPipes } = data || {};
  const bigUnitConfig = unitFactor?.bigUnitValue?.[units] || { value: 1000, label: 'km' };
  const unitLabel = bigUnitConfig.label === 'mile' ? 'miles' : bigUnitConfig.label;
  const conversionFactor = bigUnitConfig.value;

  const convertedCovered = coveredPipes ? (coveredPipes * 1000) / conversionFactor : null;
  const convertedTotal = totalPipes ? (totalPipes * 1000) / conversionFactor : null;
  const hasData = convertedCovered !== null || convertedTotal !== null;

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [pipeCoverage, setPipeCoverage] = useState('');

  const headerActions = (user?.isAQS || user?.dashboardSettings) ? (
    <Tooltip title={t('project_settings')}>
      <IconButton
        size="small"
        onClick={() => setSettingsOpen(true)}
        sx={{
          backgroundColor: 'white',
          border: '1px solid #e8e8e8',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          padding: '4px',
          width: '24px',
          height: '24px',
          '&:hover': { backgroundColor: '#f5f5f5' }
        }}
      >
        <SettingsIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  ) : null;

  return (
    <BaseCard title={t('pipe_coverage')} icon={<PlumbingIcon />} className="compact-card" headerActions={headerActions}>
      <Box display="flex" alignItems="center" justifyContent="center" height="100%" gap={1.5}>
        {hasData ? (
          <>
            <Box sx={statBoxStyle}>
              <Typography className="stat-value" sx={{ ...statValueStyle, color: ZIMA_BLUE }}>
                {convertedCovered !== null ? convertedCovered.toFixed(1) : 'N/A'}
              </Typography>
              <Typography sx={statLabelStyle}>
                {unitLabel} {t('calculated')}
              </Typography>
            </Box>
            <Box sx={statBoxStyle}>
              <Typography className="stat-value" sx={{ ...statValueStyle, color: ZIMA_BLUE }}>
                {convertedTotal !== null ? convertedTotal.toFixed(1) : 'N/A'}
              </Typography>
              <Typography sx={statLabelStyle}>
                {unitLabel} {t('potential')}
              </Typography>
            </Box>
          </>
        ) : (
          <Typography sx={{ fontWeight: 600, fontSize: '1.6rem' }}>N/A</Typography>
        )}
      </Box>

      <AppModal
        closeIcon
        open={settingsOpen}
        maxWidth='lg'
        style={{ zIndex: 9999 }}
        onClose={() => setSettingsOpen(false)}
        title={t('potential_pipe_coverage')}        
        content={(
          <div style={{ marginBottom: '24px' }}>
            <input
              type="number"
              value={pipeCoverage}
              onChange={(e) => setPipeCoverage(e.target.value)}
              placeholder={t('enter_value')}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                marginTop: '8px',
              }}
              step="0.1"
              min="0"
            />
            <p style={{
              fontSize: '12px',
              color: '#666',
              marginTop: '4px',
            }}>{t('potential_pipe_coverage_helper', { unit: unitLabel })}</p>
            <Button
              variant='contained'
              onClick={() => updateProjectPipeCoverage(projectData.ID, pipeCoverage)}
            >{t('save')}</Button>
          </div>
        )}
      />
    </BaseCard>
  );
};

PipeCoverageCard.propTypes = {
  data: PropTypes.shape({
    total: PropTypes.number,
    covered: PropTypes.number
  }),
  t: PropTypes.func.isRequired,
  units: PropTypes.string,
  unitFactor: PropTypes.object
};

export default PipeCoverageCard;
